import React from 'react'
import { useDispatch } from 'react-redux'
import { visionInsuranceClaimFormEdit } from '../../../../../../../../redux/actions/addOns/rpa/vision_insurance/visionInsuranceClaimFormAction'
import { formatVersantCodesLabel, normalizeVersantCodes } from './versantClaimHelper'

const SERVICE_KEYS = {
  Exam: 'exam',
  Frames: 'frame',
  'Contact Lens': 'contact_lens',
  'Contact Lens Fit & F/U': 'contact_lens_fit',
}

const display = (value) => (value === 0 || value ? value : '-')

const OverviewField = ({ label, value }) => (
  <div className="col-6 mt-5">
    <h6 className="mb-4">{label}</h6>
    <p className="text-muted mb-3 text-break">{display(value)}</p>
  </div>
)

const RxValue = ({ label, value }) => (
  <div className="col-6 col-md-3 mb-4">
    <div className="text-muted small mb-1">{label}</div>
    <div className="font-weight-bold text-break">{display(value)}</div>
  </div>
)

const getServiceCodes = (visionInsuranceClaimForm, serviceType) => {
  const serviceKey = SERVICE_KEYS[serviceType]
  const serviceData = visionInsuranceClaimForm[serviceKey] || {}

  const diagnosisLabels = serviceData.diagnosis_code || []
  const procedureLabels = serviceData.procedure_code || []

  // Fallback to UI state if the saved data isn't present
  const diagnosisCodes = normalizeVersantCodes(
    visionInsuranceClaimForm.services?.diagnosisCodes || visionInsuranceClaimForm.diagnosis_codes
  )
  const procedureCodes = normalizeVersantCodes(
    visionInsuranceClaimForm.services?.procedureCodes || visionInsuranceClaimForm.procedure_codes
  )

  const diagnosisDisplay = diagnosisLabels.length
    ? diagnosisLabels.join(', ')
    : (formatVersantCodesLabel(diagnosisCodes) || '-')

  const procedureDisplay = procedureLabels.length
    ? procedureLabels.join(', ')
    : (formatVersantCodesLabel(procedureCodes) || '-')

  return { diagnosisDisplay, procedureDisplay }
}

function ServiceOverview({ title, diagnosisDisplay, procedureDisplay, extraFields = [] }) {
  return (
    <div className="mt-7 border-top pt-6">
      <h5 className="mb-5">{title} Details</h5>
      <div className="row">
        {extraFields.map(([label, value], index) => (
          <OverviewField key={index} label={label} value={value} />
        ))}
        <OverviewField label="Diagnosis Codes" value={diagnosisDisplay} />
        <OverviewField label="Procedure Codes" value={procedureDisplay} />
      </div>
    </div>
  )
}

function SpectacleLensOverview({ spectacle_lens = {} }) {
  const rx = spectacle_lens.perscriptions || {}
  const lenInfo = spectacle_lens.len_information || {}
  const frame = spectacle_lens.frame || {}
  const design = lenInfo.lens_design || {}

  return (
    <div className="mt-7 border-top pt-6">
      <h5 className="mb-5">Spectacle Lens Details</h5>

      <h6 className="mb-4 mt-5">Prescription Information</h6>
      <div className="row">
        <div className="col-12 col-lg-6 mb-5">
          <div className="border rounded p-5 h-100 bg-white">
            <h6 className="mb-5 font-weight-bold">Right (OD)</h6>
            <div className="row">
              <RxValue label="Sphere" value={rx.right_sphere} />
              <RxValue label="Cylinder" value={rx.right_cyclinder} />
              <RxValue label="Axis" value={rx.right_axis} />
              <RxValue label="Addition" value={rx.right_addition} />
              <RxValue label="Prism In/Out" value={`${display(rx.right_prism_value_1)} ${display(rx.right_prism_base_1)}`.trim()} />
              <RxValue label="Prism Up/Down" value={`${display(rx.right_prism_value_2)} ${display(rx.right_prism_base_2)}`.trim()} />
              <RxValue label="Distance PD" value={rx.right_distance_pd} />
              <RxValue label="Near PD" value={rx.right_near_pd} />
              <RxValue label="Height" value={rx.right_height_pd} />
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-6 mb-5">
          <div className="border rounded p-5 h-100 bg-white">
            <h6 className="mb-5 font-weight-bold">Left (OS)</h6>
            <div className="row">
              <RxValue label="Sphere" value={rx.left_sphere} />
              <RxValue label="Cylinder" value={rx.left_cyclinder} />
              <RxValue label="Axis" value={rx.left_axis} />
              <RxValue label="Addition" value={rx.left_addition} />
              <RxValue label="Prism In/Out" value={`${display(rx.left_prism_value_1)} ${display(rx.left_prism_base_1)}`.trim()} />
              <RxValue label="Prism Up/Down" value={`${display(rx.left_prism_value_2)} ${display(rx.left_prism_base_2)}`.trim()} />
              <RxValue label="Distance PD" value={rx.left_distance_pd} />
              <RxValue label="Near PD" value={rx.left_near_pd} />
              <RxValue label="Height" value={rx.left_heighte_pd} />
            </div>
          </div>
        </div>
      </div>

      <h6 className="mb-4 mt-7">Lab/Lens Information</h6>
      <div className="row">
        <OverviewField label="Laboratory-Acct#" value={lenInfo.lab} />
        <OverviewField label="Lens Type" value={lenInfo.len_type} />
        <OverviewField label="Job Type" value={lenInfo.job_type} />
        <OverviewField label="Lens Material" value={design.lens_material_name} />
        <OverviewField label="Lens Design" value={design.lens_design} />
        <OverviewField label="Lens Tier" value={design.base} />
      </div>

      <h6 className="mb-4 mt-7">Frame Information</h6>
      <div className="row">
        <OverviewField label="Manufacturer" value={frame.manufacturer} />
        <OverviewField label="Brand" value={frame.brand} />
        <OverviewField label="Model" value={frame.model} />
        <OverviewField label="Color" value={frame.color} />
        <OverviewField label="Eye Size" value={frame.eye_size} />
        <OverviewField label="Temple Length" value={frame.temple_lenght} />
        <OverviewField label="Frame Type" value={frame.frame_type} />
        <OverviewField label="Thickness Type" value={frame.thickenss_type} />
        <OverviewField label="Left Thickness" value={frame.left_thickness} />
        <OverviewField label="Right Thickness" value={frame.right_thickness} />
        <OverviewField label="A Box" value={frame.a_box} />
        <OverviewField label="B Box" value={frame.b_box} />
        <OverviewField label="DBL" value={frame.dbl} />
        <OverviewField label="ED" value={frame.ed} />
      </div>
    </div>
  )
}

export default function VersantClaimFormOverview({ visionInsuranceClaimForm }) {
  const dispatch = useDispatch()
  const { eligibility } = visionInsuranceClaimForm
  const activeServices = eligibility?.data?.filter((item) => item.active).map((item) => item.service_type) || []

  const dilationPerformed = (visionInsuranceClaimForm.dilationPerformed ?? visionInsuranceClaimForm.versant?.dilationPerformed) ? 'Yes' : 'No'
  const isMemberDiabetic = (visionInsuranceClaimForm.isMemberDiabetic ?? visionInsuranceClaimForm.versant?.isMemberDiabetic) ? 'Yes' : 'No'
  const diabeticCodes = visionInsuranceClaimForm.diabeticCodes || visionInsuranceClaimForm.versant?.diabeticCodes || []
  const diseaseReporting = diabeticCodes[0]?.description || '-'
  const additionalConditional = diabeticCodes[1]?.description || '-'

  return (
    <div className="mt-7">
      <div className="bg-white p-6 border rounded">
        <div className="d-flex justify-content-between">
          <h3>Versant Insurance Claim Form</h3>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => dispatch(visionInsuranceClaimFormEdit({ formView: 'form', activeModule: 'first' }))}
          >
            Edit Claim Form
          </button>
        </div>

        <div className="mt-7">
          <h5 className="mb-5">Selected Services</h5>
          <div className="row">
            {activeServices.map((service) => (
              <div key={service} className="col-3">
                <p className="text-muted mb-3 font-weight-bold">{service}</p>
              </div>
            ))}
          </div>
        </div>

        {activeServices.map((service) => {
          if (service === 'Exam') {
            const { diagnosisDisplay, procedureDisplay } = getServiceCodes(visionInsuranceClaimForm, 'Exam')
            const examFields = [
              ['Dilated Eye Exam (OR) Fundus Photography performed', dilationPerformed],
              ['Is the member diabetic?', isMemberDiabetic],
              ['Disease Reporting Diagnosis', diseaseReporting],
              ['Additional conditional list', additionalConditional],
            ]
            return (
              <ServiceOverview
                key={service}
                title="Exam"
                diagnosisDisplay={diagnosisDisplay}
                procedureDisplay={procedureDisplay}
                extraFields={examFields}
              />
            )
          }

          if (service === 'Frames') {
            const { diagnosisDisplay, procedureDisplay } = getServiceCodes(visionInsuranceClaimForm, 'Frames')
            return (
              <ServiceOverview
                key={service}
                title="Frames"
                diagnosisDisplay={diagnosisDisplay}
                procedureDisplay={procedureDisplay}
              />
            )
          }

          if (service === 'Contact Lens') {
            const { diagnosisDisplay, procedureDisplay } = getServiceCodes(visionInsuranceClaimForm, 'Contact Lens')
            return (
              <ServiceOverview
                key={service}
                title="Contact Lens"
                diagnosisDisplay={diagnosisDisplay}
                procedureDisplay={procedureDisplay}
              />
            )
          }

          if (service === 'Contact Lens Fit & F/U') {
            const { diagnosisDisplay, procedureDisplay } = getServiceCodes(visionInsuranceClaimForm, 'Contact Lens Fit & F/U')
            return (
              <ServiceOverview
                key={service}
                title="Contact Lens Fit & F/U"
                diagnosisDisplay={diagnosisDisplay}
                procedureDisplay={procedureDisplay}
              />
            )
          }

          if (service === 'Spectacle Lens' && visionInsuranceClaimForm.spectacle_lens) {
            return (
              <SpectacleLensOverview
                key={service}
                spectacle_lens={visionInsuranceClaimForm.spectacle_lens}
              />
            )
          }

          return null
        })}
      </div>
    </div>
  )
}
